import React, { useEffect, useState, useRef } from "react";
import moment from "moment";

interface Props {
  label?: string;
  name: string;
  value: string | null;   // stored as YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss or MM/DD/YYYY
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  filterDate?: (date: Date) => boolean; // Optional function to filter dates
  allowFutureDates?: boolean; // Allow future dates (default: false)
  hideIcon?: boolean; // Optional prop to hide the calendar icon
}

// Convert backend formats → MM-DD-YYYY (for UI). Returns "" when there is no usable date.
function toDisplay(value: string | null): string {
  if (!value) return "";

  let mm = "", dd = "", yyyy = "";

  if (value.includes("/")) {
    // Handle MM/DD/YYYY or similar with /
    const [m, d, y] = value.split("/");
    mm = m.padStart(2, "0");
    dd = d.padStart(2, "0");
    yyyy = y.split("T")[0]; // Remove time if present
  } else if (value.includes("-")) {
    // Handle YYYY-MM-DD or ISO
    const clean = value.split("T")[0];
    const parts = clean.split("-");
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      [yyyy, mm, dd] = parts;
    } else {
      // MM-DD-YYYY (unexpected but possible)
      [mm, dd, yyyy] = parts;
    }
  }

  if (mm && dd && yyyy) {
    return `${mm}-${dd}-${yyyy}`; // ✅ MM-DD-YYYY in UI
  }

  // Fallback for any other weird strings
  const d = new Date(value);
  if (!isNaN(d.getTime())) {
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${m}-${day}-${year}`;
  }

  return "";
}

export default function DateInput({ label, name, value, onChange, error, filterDate, allowFutureDates = false, hideIcon = false }: Props) {
  const [display, setDisplay] = useState("");
  const hiddenDateRef = useRef<HTMLInputElement>(null);

  // Today's date in YYYY-MM-DD (local time) for the calendar's max limit
  const today = moment().format("YYYY-MM-DD");

  useEffect(() => {
    setDisplay(toDisplay(value));
  }, [value]);

  // Emit with type "date" so form hooks that map "" → null for date fields see it as one
  const emit = (nextValue: string) => {
    onChange({
      target: { name, value: nextValue, type: "date" }
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  // Push an empty value up so the parent form actually forgets the date
  const clearValue = () => {
    setDisplay("");
    emit("");
  };

  // When user leaves the text input
  const handleBlur = () => {
    const cleaned = display.replace(/[^0-9]/g, "");

    // Field was wiped by the user → clear the stored date too
    if (!cleaned) {
      if (value) clearValue();
      else setDisplay("");
      return;
    }

    // Incomplete/garbage input → restore whatever is actually stored
    if (cleaned.length !== 8) {
      setDisplay(toDisplay(value));
      return;
    }

    // ✅ Interpret as MMDDYYYY
    const mm = cleaned.substring(0, 2);
    const dd = cleaned.substring(2, 4);
    const yyyy = cleaned.substring(4);

    const backend = `${yyyy}-${mm}-${dd}`;   // stored as YYYY-MM-DD
    const uiValue = `${mm}-${dd}-${yyyy}`;   // shown as MM-DD-YYYY
    const selectedDate = moment(backend, "YYYY-MM-DD").toDate(); // local midnight

    // 🚫 Prevent future dates (only if allowFutureDates is false)
    if (!allowFutureDates && backend > today) {
      alert("Future dates are not allowed.");
      clearValue();
      return;
    }

    // 🚫 Apply filterDate if provided
    if (filterDate && !filterDate(selectedDate)) {
      alert("Please Select date between Mon - Fri");
      clearValue();
      return;
    }

    setDisplay(uiValue);
    emit(backend);
  };

  // Keep user typing
  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplay(e.target.value);
  };

  // Open native picker
  const openCalendar = () => {
    hiddenDateRef.current?.showPicker?.();
  };

  // When selecting from calendar
  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const backend = e.target.value; // YYYY-MM-DD

    // "Clear" in the native picker fires change with an empty value → clear the stored date
    if (!backend) {
      clearValue();
      return;
    }

    const selectedDate = moment(backend, "YYYY-MM-DD").toDate(); // local midnight

    // 🚫 Prevent future dates (only if allowFutureDates is false)
    if (!allowFutureDates && backend > today) {
      alert("Future dates are not allowed.");
      clearValue();
      return;
    }

    // 🚫 Apply filterDate if provided
    if (filterDate && !filterDate(selectedDate)) {
      alert("Please Select date between Mon - Fri");
      clearValue();
      return;
    }

    const [yyyy, mm, dd] = backend.split("-");
    const uiValue = `${mm}-${dd}-${yyyy}`; // ✅ MM-DD-YYYY

    setDisplay(uiValue);
    emit(backend);
  };

  return (
    <div className="mb-0">
      {label && <label className="form-label mb-1">{label}</label>}

      <div style={{ position: "relative" }}>
        {/* Visible input */}
        <input
          type="text"
          className="form-control"
          placeholder="MM-DD-YYYY"
          value={display}
          onChange={handleTyping}
          onBlur={handleBlur}
        // style={hideIcon ? { paddingRight: "35px" } : {}}
        />

        {/* Calendar Icon */}
        {/* {!hideIcon && ( */}
        <span
          onClick={openCalendar}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            fontSize: "20px",
            opacity: hideIcon ? 0.1 : 0.7,
          }}
        >
          📅
        </span>
        {/* )} */}

        {/* Hidden native date input */}
        <input
          type="date"
          ref={hiddenDateRef}
          value={value ? (value.includes("-") ? value.split("T")[0] : moment(value, ["MM-DD-YYYY", "MM/DD/YYYY"]).format("YYYY-MM-DD")) : ""}
          onChange={handleNativeDateChange}
          max={allowFutureDates ? undefined : today}                 // 🚫 Prevent future dates only if not allowed
          style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
        />
      </div>

      {error && <div className="text-danger">{error}</div>}
    </div>
  );
}
